import { createApiClient } from './client';
import { saveAuthSession } from '../utils/authSession';
import {
  PILOT_API_BASE_URL,
  PILOT_APP_VERSION,
  PILOT_AUTH_PATH,
  PILOT_CONSENT_VERSION,
  PILOT_FEATURE_ENABLED,
} from '../config/internalTestConfig';
import { submitPilotEventBatch } from './pilotEvents';
import { activatePilotTelemetry, createPilotEventId, getPilotDeviceClass } from '../utils/pilotTelemetry';
import { createPilotEvent, type PilotDeviceClass, type PilotEvent } from '../utils/internalTestPolicy';
import { isValidPilotAccountId, normalizePilotAccountId } from '../utils/pilotAccountContract';

type PilotLoginInput = {
  accountId: string;
  temporaryPassword: string;
  consentAccepted: boolean;
};

type PilotLoginResponse = { access_token?: string; token_type?: string };

type InternalTestAuthDependencies = {
  enabled: boolean;
  postLogin: (path: string, body: { account_id: string; temporary_password: string }) => Promise<PilotLoginResponse>;
  submitInitialConsent: (accessToken: string, events: PilotEvent[]) => Promise<void>;
  saveSession: (session: { accessToken: string; tokenType: string | null }) => Promise<unknown>;
  activateTelemetry: (
    context: { activatedAt: string; sessionId: string },
    queuedEvents: PilotEvent[]
  ) => Promise<void>;
  createEventId: () => string;
  now: () => Date;
  appVersion: string;
  deviceClass: PilotDeviceClass;
};

export const createInternalTestAuth = ({
  enabled,
  postLogin,
  submitInitialConsent,
  saveSession,
  activateTelemetry,
  createEventId,
  now,
  appVersion,
  deviceClass,
}: InternalTestAuthDependencies) => ({
  async login({ accountId, temporaryPassword, consentAccepted }: PilotLoginInput) {
    if (!enabled) throw new Error('Staging pilot mode is disabled');
    if (!consentAccepted) throw new Error('Pilot analytics consent is required');
    const normalizedAccountId = normalizePilotAccountId(accountId);
    if (!isValidPilotAccountId(normalizedAccountId) || temporaryPassword.length < 16) {
      throw new Error('Pilot credentials are invalid');
    }

    const response = await postLogin(PILOT_AUTH_PATH, {
      account_id: normalizedAccountId,
      temporary_password: temporaryPassword,
    });
    if (!response.access_token) throw new Error('Pilot login did not return an access token');

    const activatedAt = now();
    const eventAt = activatedAt.toISOString();
    const sessionId = createEventId();
    const common = {
      occurredDay: 0,
      eventAt,
      appVersion,
      deviceClass,
      consentVersion: PILOT_CONSENT_VERSION,
    };
    const events = [
      createPilotEvent({ ...common, eventId: createEventId(), eventName: 'consent_presented', eventStatus: 'presented' }),
      createPilotEvent({ ...common, eventId: createEventId(), eventName: 'consent_granted', eventStatus: 'granted' }),
      createPilotEvent({ ...common, eventId: createEventId(), eventName: 'pilot_slot_activated', eventStatus: 'success' }),
      createPilotEvent({ ...common, eventId: createEventId(), sessionId, eventName: 'session_started', eventStatus: 'success' }),
    ];

    let queuedEvents: PilotEvent[] = [];
    try {
      await submitInitialConsent(response.access_token, events);
    } catch {
      queuedEvents = events;
    }
    const session = await saveSession({ accessToken: response.access_token, tokenType: response.token_type ?? null });
    try {
      await activateTelemetry({ activatedAt: eventAt, sessionId }, queuedEvents);
    } catch {
      // Telemetry storage and delivery must never invalidate an authenticated session.
    }
    return session;
  },
});

const authClient = createApiClient({ baseUrl: PILOT_API_BASE_URL ?? '' });

const pilotAuth = createInternalTestAuth({
  enabled: PILOT_FEATURE_ENABLED,
  postLogin: async (path, body) => (await authClient.post<PilotLoginResponse, typeof body>(path, body)).data,
  submitInitialConsent: async (accessToken, events) => submitPilotEventBatch(events, accessToken),
  saveSession: saveAuthSession,
  activateTelemetry: activatePilotTelemetry,
  createEventId: createPilotEventId,
  now: () => new Date(),
  appVersion: PILOT_APP_VERSION,
  deviceClass: getPilotDeviceClass(),
});

export const loginWithPilotAccount = (input: PilotLoginInput) => pilotAuth.login(input);
